// Tablero de consumo de IA sobre TanStack Query. Mismo patrón que
// `useMantenedorDeIa`/`useFeedback`: la cache de la query es la fuente de
// verdad y las mutaciones invalidan la key en vez de parchear a mano.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  actualizarPrecioDeIaRequest,
  crearPrecioDeIaRequest,
  eliminarPrecioDeIaRequest,
  fetchConsumosDeIa,
  fetchPreciosDeIa,
  fetchResumenDeConsumoDeIa,
  type FiltrosDelLogDeIa,
  type FiltrosDelResumenDeIa,
  type IaPrecioInput,
} from "@/lib/consumoDeIa";

export const RESUMEN_DE_CONSUMO_DE_IA_KEY = ["ia", "consumos", "resumen"] as const;
export const LOG_DE_CONSUMO_DE_IA_KEY = ["ia", "consumos", "log"] as const;
export const PRECIOS_DE_IA_KEY = ["ia", "precios"] as const;

/** Los filtros van DENTRO de la key porque filtra el BACKEND: un rango ya
    visitado no se vuelve a pedir. */
export function useResumenDeConsumoDeIa(filtros: FiltrosDelResumenDeIa) {
  return useQuery({
    queryKey: [...RESUMEN_DE_CONSUMO_DE_IA_KEY, filtros],
    queryFn: () => fetchResumenDeConsumoDeIa(filtros),
  });
}

/** `placeholderData` conserva la página anterior mientras llega la nueva: sin
    eso, cambiar de página vaciaría la tabla que se está leyendo. */
export function useLogDeConsumoDeIa(filtros: FiltrosDelLogDeIa) {
  return useQuery({
    queryKey: [...LOG_DE_CONSUMO_DE_IA_KEY, filtros],
    queryFn: () => fetchConsumosDeIa(filtros),
    placeholderData: (anterior) => anterior,
  });
}

/** Sin paginar: son pocas filas por definición — un proveedor cambia de
    tarifa unas pocas veces al año. */
export function usePreciosDeIa() {
  return useQuery({
    queryKey: PRECIOS_DE_IA_KEY,
    queryFn: fetchPreciosDeIa,
  });
}

export function useCrearPrecioDeIa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: IaPrecioInput) => crearPrecioDeIaRequest(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PRECIOS_DE_IA_KEY }),
  });
}

export function useActualizarPrecioDeIa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: IaPrecioInput }) =>
      actualizarPrecioDeIaRequest(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PRECIOS_DE_IA_KEY }),
  });
}

export function useEliminarPrecioDeIa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => eliminarPrecioDeIaRequest(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PRECIOS_DE_IA_KEY }),
  });
}
